import { useMemo } from "react"
import type { RequestStatus } from "@shared/api"
import { REQUEST_STATUS_VALUES } from "@shared/api"
import { useApi } from "../hooks/useApi"
import type { QueryStateApi } from "../hooks/useQueryState"
import { api } from "../services/api"
import { ErrorState, SkeletonRows } from "../components/ui"
import {
  formatDate,
  formatDateTime,
  formatHours,
  monthLabel,
  NO_DEPARTMENT,
  recentMonths,
} from "../lib/format"
import "../styles/table.scss"

/**
 * El DETALLE del tablero: cada solicitud del mes, con su justificación.
 *
 * A diferencia de OvertimePage entran TODOS los estados — esta vista es "qué se
 * pidió y qué se decidió", no "qué se paga" — con un filtro por estado además de
 * los de mes y departamento, que se COMPARTEN con la otra pestaña (misma clave
 * del query string, así cambiar de pestaña conserva lo elegido).
 *
 * Sigue sin haber escrituras: las decisiones se toman en Slack.
 */

/** Etiquetas de estado en castellano. Son UI: el contrato (shared/api) viaja en inglés. */
const STATUS_LABEL: Record<RequestStatus, string> = {
  pending: "Pendiente",
  approved: "Aprobada",
  partially_approved: "Parcial",
  rejected: "Rechazada",
}

const COLUMNS = 9

export function RequestsPage({ query, setQuery }: QueryStateApi) {
  const months = useMemo(() => recentMonths(), [])
  // Mismo clamp que OvertimePage: un ?month= inválido o viejo cae en el mes actual.
  const month = query.month && months.includes(query.month) ? query.month : months[0]
  const department = query.department ?? null
  // El estado sí se valida contra la lista local (es un enum del contrato, no
  // texto libre): basura en la URL = "Todos", y nunca se manda — un valor
  // inventado sería un 400 del servidor, no cero filas.
  const status: RequestStatus | null = (REQUEST_STATUS_VALUES as readonly string[]).includes(
    query.status ?? ""
  )
    ? (query.status as RequestStatus)
    : null

  const { data, error, loading, isRefetching, refetch } = useApi(
    (signal) => api.requests(month, department, status, signal),
    [month, department, status]
  )

  // Mismo fallback que OvertimePage: un departamento aplicado que no está entre
  // las opciones del mes se sigue ofreciendo mientras esté aplicado.
  const departmentOptions = data
    ? department && !data.departments.includes(department)
      ? [department, ...data.departments]
      : data.departments
    : department
      ? [department]
      : []

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Solicitudes</h1>
          <p className="page-subtitle">
            Todas las solicitudes con fecha de trabajo en {monthLabel(month)}
            {department ? ` · ${department}` : ""}
            {status ? ` · estado «${STATUS_LABEL[status]}»` : ""}, con su justificación.
            Las decisiones se siguen tomando en Slack.
          </p>
        </div>
        <div className="page-actions">
          <div className="month-picker">
            <label htmlFor="month">Mes</label>
            <select
              id="month"
              value={month}
              onChange={(e) => setQuery({ month: e.target.value })}
            >
              {months.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </select>
          </div>
          {departmentOptions.length > 0 && (
            <div className="month-picker">
              <label htmlFor="department">Departamento</label>
              <select
                id="department"
                value={department ?? ""}
                onChange={(e) => setQuery({ department: e.target.value || undefined })}
              >
                <option value="">Todos</option>
                {departmentOptions.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Siempre visible, a diferencia del de departamento: las opciones son
              fijas (el enum del contrato), no dependen de los datos del mes. */}
          <div className="month-picker">
            <label htmlFor="status">Estado</label>
            <select
              id="status"
              value={status ?? ""}
              onChange={(e) => setQuery({ status: e.target.value || undefined })}
            >
              <option value="">Todos</option>
              {REQUEST_STATUS_VALUES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
          <button type="button" className="btn btn-sm" onClick={refetch} disabled={isRefetching}>
            {isRefetching ? "Actualizando…" : "Actualizar"}
          </button>
        </div>
      </div>

      {error ? (
        <div className="card">
          <ErrorState
            title="No se pudieron cargar las solicitudes"
            message={error.message}
            onRetry={refetch}
          />
        </div>
      ) : (
        <>
          <div className={`table-wrap${isRefetching ? " is-refetching" : ""}`}>
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Fecha</th>
                  <th scope="col">Empleado</th>
                  <th scope="col">Departamento</th>
                  <th scope="col" className="numeric">
                    Solicitadas
                  </th>
                  <th scope="col" className="numeric">
                    Aprobadas
                  </th>
                  <th scope="col">Estado</th>
                  <th scope="col">Justificación</th>
                  <th scope="col">Decisión</th>
                  <th scope="col">Cargó</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonRows rows={5} cols={COLUMNS} />
                ) : data && data.rows.length > 0 ? (
                  data.rows.map((r) => (
                    <tr key={r.id}>
                      <td className="nowrap">{formatDate(r.work_date)}</td>
                      <td>{r.employee_name}</td>
                      <td>{r.department_name ?? NO_DEPARTMENT}</td>
                      <td className="numeric">{formatHours(r.hours_requested)}</td>
                      {/* null en pending/rejected: no hay horas aprobadas que mostrar. */}
                      <td className="numeric">
                        {r.hours_approved === null ? "—" : formatHours(r.hours_approved)}
                      </td>
                      <td className="nowrap">
                        <span className={`pill pill--${r.status}`}>{STATUS_LABEL[r.status]}</span>
                      </td>
                      <td className="justification">{r.justification}</td>
                      <td className="justification">
                        {r.status === "pending" ? (
                          "—"
                        ) : (
                          <>
                            {r.decision_comment}
                            <span className="cell-sub">
                              por {r.decided_by_name ?? "—"}
                              {r.decided_at ? ` · ${formatDateTime(r.decided_at)}` : ""}
                            </span>
                          </>
                        )}
                      </td>
                      <td title={`Cargada el ${formatDateTime(r.requested_at)}`}>
                        {r.requester_name}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="empty" colSpan={COLUMNS}>
                      <div className="state-title">
                        Sin solicitudes en {monthLabel(month)}
                        {department ? ` en ${department}` : ""}
                        {status ? ` con estado «${STATUS_LABEL[status]}»` : ""}
                      </div>
                      {department || status
                        ? "Puede que el mes tenga solicitudes fuera de este filtro: probá con «Todos»."
                        : "Acá aparecen todas: pendientes, aprobadas, parciales y rechazadas."}
                    </td>
                  </tr>
                )}
              </tbody>
              {data && data.rows.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={3}>
                      Total · {data.total_requests}{" "}
                      {data.total_requests === 1 ? "solicitud" : "solicitudes"}
                    </td>
                    <td className="numeric">{formatHours(data.total_hours_requested)}</td>
                    <td className="numeric">{formatHours(data.total_hours_approved)}</td>
                    <td colSpan={4}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {data?.truncated && (
            <p className="freshness">
              El mes superó el tope de la consulta: se muestran las primeras 500 solicitudes
              por fecha, y los filtros y totales se aplican solo sobre esas.
            </p>
          )}
          {data && (
            <p className="freshness">
              Datos al{" "}
              {new Date(data.generated_at).toLocaleTimeString("es", {
                hour: "2-digit",
                minute: "2-digit",
              })}
              .
            </p>
          )}
        </>
      )}
    </>
  )
}
